<template>
    <div class="pl-page inv-page">

        <!-- SEARCH TOOLBAR -->
        <div class="compact-toolbar inv-toolbar">
            <div class="toolbar-group">
                <span class="inv-toolbar-label">
                    <span class="pc-ico"><b-icon icon="plus-circle"></b-icon></span>
                    Add Inventory
                </span>
            </div>
            <div class="toolbar-group inv-search-group">
                <vue-simple-suggest
                    v-model="keyword"
                    display-attribute="product_name"
                    value-attribute="product_id_product_sku"
                    :max-suggestions="200"
                    :list="searchProducts"
                    :filter-by-query="false"
                    @select="add_to_cache"
                    :min-length="2"
                    ref="product_sku"
                    class="inv-search-input"
                    placeholder="Search by product name or SKU…"
                >
                    <div slot="suggestion-item" slot-scope="{ suggestion }">
                        <span>
                            {{ suggestion.product_name }}
                            <span class="suggestion-sku">[{{ suggestion.product_sku }}]</span>
                        </span>
                    </div>
                </vue-simple-suggest>
            </div>
        </div>

        <!-- LOADING / EMPTY -->
        <div v-if="items_being_loaded" class="inv-empty">
            <b-spinner variant="primary" label="Loading"></b-spinner>
            <p>Loading products…</p>
        </div>
        <div v-else-if="items_to_add.length === 0" class="inv-empty">
            <span class="pc-ico inv-empty-ico"><b-icon icon="box-seam"></b-icon></span>
            <p>Search for a product above to begin adding inventory.</p>
        </div>

        <!-- ITEMS TO ADD -->
        <div v-else class="pc-list inv-list">
            <article
                v-for="(item, idx) in items_to_add"
                :key="item.idx"
                class="pc-card inv-card inv-add-card"
            >
                <!-- LOADING OVERLAY -->
                <div v-if="item.loading" class="inv-add-loading">
                    <b-spinner variant="primary"></b-spinner>
                    <p>Adding to inventory…</p>
                </div>

                <!-- ITEM SUMMARY -->
                <div v-else class="pc-sum inv-sum">
                    <div class="inv-col inv-col-img">
                        <div v-if="productImage(item)" class="inv-img-btn">
                            <img :src="productImage(item)" :alt="item.product_sku" />
                        </div>
                        <div v-else class="inv-img-empty">
                            <span class="pc-ico"><b-icon icon="image"></b-icon></span>
                        </div>
                    </div>

                    <div class="inv-col inv-col-product">
                        <div class="pc-name inv-name" :title="item.product_name">
                            {{ item.product_name }}
                        </div>
                        <div class="inv-sku-row">
                            <span class="inv-sku-label">SKU</span>
                            <span class="inv-sku">{{ item.product_sku }}</span>
                        </div>
                    </div>

                    <div class="inv-col inv-col-loc">
                        <b-form-select
                            size="sm"
                            class="pc-select inv-loc-select"
                            :options="availableLocations"
                            v-model="item.locsub"
                        ></b-form-select>
                    </div>

                    <div class="inv-col inv-col-num inv-col-qty-input">
                        <b-form-input
                            size="sm"
                            class="pc-input inv-qty-input"
                            v-model="item.quantity"
                            type="number"
                            min="1"
                            placeholder="Qty"
                        ></b-form-input>
                    </div>

                    <div class="inv-col inv-col-num">
                        <span class="inv-stat-label">In Stock</span>
                        <span class="inv-v">{{ item.in_stock }}</span>
                    </div>
                    <div class="inv-col inv-col-num">
                        <span class="inv-stat-label">Reserved</span>
                        <span class="inv-v is-reserved">{{ item.reserved }}</span>
                    </div>
                    <div class="inv-col inv-col-num">
                        <span class="inv-stat-label">Available</span>
                        <span class="inv-v is-good">{{ item.available }}</span>
                    </div>

                    <div class="inv-col inv-col-actions">
                        <button
                            type="button"
                            class="pc-btn is-primary"
                            @click="add_product_to_inventory(item)"
                            v-b-tooltip.hover.top="'Add to inventory'"
                        >
                            <span class="pc-ico"><b-icon icon="plus-lg"></b-icon></span>
                            ADD
                        </button>
                        <button
                            type="button"
                            class="pc-icon-btn is-danger"
                            @click="remove_item(item)"
                            v-b-tooltip.hover.top="'Remove from list'"
                        >
                            <span class="pc-ico"><b-icon icon="x-lg"></b-icon></span>
                        </button>
                    </div>
                </div>
            </article>
        </div>
    </div>
</template>

<script>
import Requestor from "@/utilities/Requestor";
import VueSimpleSuggest from "vue-simple-suggest";
import "vue-simple-suggest/dist/styles.css";

export default {
    name: "AddInventory",
    components: { VueSimpleSuggest },
    data() {
        return {
            packet: {},
            keyword: "",
            items_being_loaded: false,
            items_to_add: [],
            searchCache: [],
        };
    },
    computed: {
        availableLocations() {
            if (!this.packet.locsubs) return [];
            return this.packet.locsubs.filter(
                (loc) => ![44, 46, 47].includes(loc.location_id)
            );
        },
    },
    methods: {
        async searchProducts(query) {
            if (!query || query.length < 2) return [];
            try {
                const response = await Requestor.getAsync(
                    "/api/product/search_products_all/",
                    { search: query }
                );
                if (response.data.error) {
                    console.error("Error searching products:", response.data.error);
                    return [];
                }
                return response.data.result || [];
            } catch (error) {
                console.error("Error searching products:", error);
                return [];
            }
        },

        add_to_cache(e) {
            const idx = this.items_to_add.length + 1;
            const item = {
                product_name: e.product_name,
                product_id: parseInt(e.product_id),
                product_sku: e.product_sku,
                locsub: null,
                quantity: 1,
                loading: false,
                idx: idx,
                username: this.packet.userDetails.username,
                add_log: true,
                available: e.available,
                in_stock: e.in_stock,
                reserved: e.reserved,
                image_url: e.image_url || null,
            };
            this.items_to_add.push(item);

            // Reset the search input
            this.keyword = "";
            if (this.$refs.product_sku) {
                this.$refs.product_sku.$el.querySelector("input").value = "";
            }
        },

        remove_item(item) {
            this.items_to_add = this.items_to_add.filter((d) => d.idx !== item.idx);
        },

        productImage(item) {
            if (!item || !item.image_url) return null;
            let url = String(item.image_url).trim();
            if (!url || url === "null" || url === "undefined") return null;
            if (url.includes("fmapi.myfurnituremecca.com")) {
                return `/api/image/image-proxy/?url=${encodeURIComponent(url)}`;
            }
            if (!/^https?:\/\//i.test(url) && !url.startsWith("/")) url = "/" + url;
            return url;
        },

        add_product_to_inventory(item) {
            const data = { ...item };

            if (!data.locsub) {
                this.$bvToast.toast("Please select a target location.", {
                    title: "Validation",
                    autoHideDelay: 2000,
                    variant: "warning",
                });
                return;
            }

            const locsub = data.locsub.split("|");
            data.location_id = parseInt(locsub[0]);
            data.sublocation_id = parseInt(locsub[1]);
            data.nitems = parseInt(data.quantity);

            if (data.product_sku.replaceAll(/\s/g, "").length !== data.product_sku.length) {
                this.$bvToast.toast("Invalid product SKU.", {
                    title: "Validation",
                    autoHideDelay: 2000,
                    variant: "warning",
                });
                return;
            }

            if (!data.nitems || data.nitems < 1) {
                this.$bvToast.toast("Please enter a valid quantity.", {
                    title: "Validation",
                    autoHideDelay: 2000,
                    variant: "warning",
                });
                return;
            }

            data.product_sku = data.product_sku.replaceAll(/\s/g, "");

            const that = this;
            const path = "/api/inventory/add_single_product_to_inventory/";

            const s = (response) => {
                if (response.data.error) {
                    that.$bvToast.toast("ERROR ADDING ITEM TO INVENTORY.", {
                        title: "Error",
                        autoHideDelay: 2000,
                        variant: "warning",
                    });
                } else {
                    const locName = that.packet.locsubsdict
                        ? that.packet.locsubsdict[item.locsub] || item.locsub
                        : item.locsub;
                    that.$bvToast.toast(
                        `Added ${item.product_name} (×${item.quantity}) to ${locName}.`,
                        { title: "Success", autoHideDelay: 2500, variant: "success" }
                    );
                    that.items_to_add = that.items_to_add.filter((d) => d.idx !== item.idx);
                }
                item.loading = false;
            };

            const f = () => {
                that.$bvToast.toast(
                    "AN ERROR OCCURRED ADDING ITEM TO INVENTORY. PLEASE TRY AGAIN LATER.",
                    { title: "Error", autoHideDelay: 2500, variant: "danger" }
                );
                item.loading = false;
            };

            item.loading = true;
            Requestor.postAsync(path, data, s, f);
        },
    },
    created() {
        this.packet = JSON.parse(localStorage.packet);
        this.packet.userDetails = JSON.parse(localStorage.userDetails);
    },
};
</script>

<style scoped>
.inv-page {
    max-width: 1100px;
    width: 96%;
    margin: 12px auto 24px;
}

/* ================================================
   TOOLBAR (matches Lookup)
   ================================================ */
.inv-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    background: var(--pl-ink) !important;
    border-radius: 16px !important;
    padding: 10px 12px !important;
    margin-bottom: 14px !important;
}
.inv-toolbar-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #fff;
    font-family: "Sora", sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 0 6px;
}
.inv-toolbar-label .pc-ico {
    color: #ffb06b;
}
.inv-search-group {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.06) !important;
    border: 1px solid rgba(255, 255, 255, 0.14) !important;
    border-radius: 12px !important;
    padding: 3px !important;
    flex: 1 1 420px;
    max-width: 640px;
    margin-left: auto;
    position: relative;
}

/* vue-simple-suggest deep styling to fit toolbar */
.inv-search-input >>> .vue-simple-suggest {
    flex: 1;
    width: 100%;
}
.inv-search-input >>> .vue-simple-suggest input {
    background: transparent !important;
    border: 0 !important;
    color: #fff !important;
    font-size: 14px;
    font-weight: 600;
    padding: 6px 10px;
    outline: none !important;
    width: 100%;
}
.inv-search-input >>> .vue-simple-suggest input::placeholder {
    color: rgba(255, 255, 255, 0.5);
}
.inv-search-input >>> .vue-simple-suggest .suggestions {
    background: #fff;
    border: 1px solid var(--pl-line);
    border-radius: 12px;
    box-shadow: 0 12px 32px -16px rgba(34, 31, 28, 0.4);
    margin-top: 6px;
    overflow: hidden;
    z-index: 20;
}
.inv-search-input >>> .vue-simple-suggest .suggestions .suggest-item {
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 600;
    color: var(--pl-ink);
    cursor: pointer;
    border-bottom: 1px solid var(--pl-line);
}
.inv-search-input >>> .vue-simple-suggest .suggestions .suggest-item:last-child {
    border-bottom: 0;
}
.inv-search-input >>> .vue-simple-suggest .suggestions .suggest-item:hover,
.inv-search-input >>> .vue-simple-suggest .suggestions .suggest-item[data-selected] {
    background: var(--pl-orange-soft);
    color: var(--pl-orange-dark);
}
.suggestion-sku {
    font-family: "Menlo", "Consolas", monospace;
    font-size: 11.5px;
    color: var(--pl-muted);
    margin-left: 4px;
}

/* ================================================
   SHARED DESIGN TOKENS (from Lookup)
   ================================================ */
.inv-list {
    --pc-ink: #221f1c;
    --pc-ink-2: #34302b;
    --pc-orange: #ee7a22;
    --pc-orange-dark: #d4620f;
    --pc-orange-soft: #fff1e4;
    --pc-teal: #0f6e6b;
    --pc-teal-soft: #e2f2f0;
    --pc-red: #c93c30;
    --pc-red-soft: #fbe9e7;
    --pc-warn: #9a6a0c;
    --pc-warn-soft: #fdf3de;
    --pc-panel: #f7f5f2;
    --pc-line: #e8e3db;
    --pc-muted: #736c63;
    --pc-head: "Sora", "Manrope", system-ui, sans-serif;
    font-family: "Manrope", system-ui, -apple-system, sans-serif;
    color: var(--pc-ink);
    background: transparent;
    border: 0 !important;
    height: auto;
    padding: 0;
}
.pc-ico {
    position: relative !important;
    display: inline-block !important;
    width: 1em !important;
    height: 1em !important;
    flex-shrink: 0;
    vertical-align: -0.125em;
    line-height: 1;
}
.pc-ico >>> svg {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    width: 1em !important;
    height: 1em !important;
    margin: 0 !important;
    transform: translate(-50%, -50%) !important;
    display: block !important;
}
.inv-list >>> svg.b-icon {
    width: 1em;
    height: 1em;
    vertical-align: -0.125em;
}

/* ================================================
   CARD + GRID
   ================================================ */
.inv-card {
    margin-bottom: 10px;
}
.pc-card {
    position: relative;
    background: #fff;
    border: 1px solid var(--pc-line);
    border-radius: 14px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.pc-card::before {
    content: "";
    position: absolute;
    left: -1px;
    top: 14px;
    width: 4px;
    height: 56px;
    border-radius: 0 4px 4px 0;
    background: var(--pc-orange);
}
.pc-card:hover {
    border-color: #d9d1c5;
    box-shadow: 0 8px 22px -16px rgba(34, 31, 28, 0.4);
}

.inv-sum {
    display: grid;
    grid-template-columns:
        100px
        minmax(0, 1.4fr)
        minmax(0, 1.2fr)
        90px
        70px 70px 70px
        170px;
    align-items: center;
    column-gap: 14px;
    padding: 14px 16px 14px 20px;
}
.inv-col {
    min-width: 0;
}

/* Image */
.inv-col-img {
    width: 100px;
    height: 100px;
    border-radius: 10px;
    background: linear-gradient(160deg, #f7f3ee, #ede8e1);
    overflow: hidden;
    position: relative;
    flex-shrink: 0;
}
.inv-img-btn {
    position: absolute;
    inset: 0;
    padding: 4px;
}
.inv-img-btn img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
}
.inv-img-empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #b9b1a6;
    font-size: 22px;
}

/* Product */
.inv-col-product {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}
.inv-name {
    font-family: var(--pc-head);
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    color: var(--pc-ink);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.inv-sku-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
}
.inv-sku-label {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--pc-muted);
}
.inv-sku {
    font-family: "Menlo", "Consolas", monospace;
    font-size: 13px;
    font-weight: 700;
    color: var(--pc-ink-2);
    word-break: break-all;
}

/* Location select */
.inv-col-loc {
    display: flex;
    align-items: center;
}
.pc-select.form-control {
    height: 38px;
    border: 1.5px solid #dcd5cb;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 700;
    color: var(--pc-ink);
    background-color: #fff;
    padding: 0 28px 0 10px;
}
.pc-select.form-control:focus {
    border-color: var(--pc-orange);
    box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18);
}

/* Quantity input */
.inv-col-qty-input {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}
.inv-qty-input {
    width: 80px;
    text-align: center;
}
.pc-input.form-control {
    height: 38px;
    border: 1.5px solid #dcd5cb;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 800;
    color: var(--pc-ink);
    background-color: #fff;
    font-variant-numeric: tabular-nums;
    padding: 0 8px;
}
.pc-input.form-control:focus {
    border-color: var(--pc-orange);
    box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18);
}

/* Numeric stats */
.inv-col-num {
    text-align: right;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.inv-stat-label {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--pc-muted);
}
.inv-v {
    font-family: var(--pc-head);
    font-size: 16px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: var(--pc-ink);
}
.inv-v.is-reserved {
    color: #d4620f;
}
.inv-v.is-good {
    color: var(--pc-teal);
}

/* Actions */
.inv-col-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
}
.pc-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    border: 1px solid var(--pc-line);
    border-radius: 9px;
    background: #fff;
    font-size: 12.5px;
    font-weight: 800;
    color: var(--pc-ink);
    cursor: pointer;
    white-space: nowrap;
}
.pc-btn.is-primary {
    background: var(--pc-orange);
    border-color: var(--pc-orange);
    color: #fff;
}
.pc-btn.is-primary:hover:not(:disabled) {
    background: var(--pc-orange-dark);
}
.pc-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.pc-icon-btn {
    position: relative;
    width: 36px;
    height: 36px;
    padding: 0 !important;
    border: 1px solid var(--pc-line) !important;
    border-radius: 9px !important;
    background: #fff !important;
    color: var(--pc-ink-2) !important;
    font-size: 14px;
    cursor: pointer;
}
.pc-icon-btn > .pc-ico {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%);
}
.pc-icon-btn.is-danger {
    color: var(--pc-red) !important;
    border-color: #f3c9c3 !important;
    background: var(--pc-red-soft) !important;
}
.pc-icon-btn.is-danger:hover {
    background: #f6d9d5 !important;
}

/* Loading overlay on card */
.inv-add-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 40px 20px;
    color: var(--pc-muted);
    font-weight: 700;
}
.inv-add-loading p {
    margin: 0;
    font-size: 13px;
}

/* ================================================
   EMPTY STATE
   ================================================ */
.inv-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 60px 20px;
    background: #fff;
    border: 1px dashed var(--pl-line);
    border-radius: 14px;
    color: var(--pl-muted);
    font-weight: 700;
}
.inv-empty p {
    margin: 0;
}
.inv-empty-ico {
    font-size: 40px;
    color: var(--pl-orange);
    opacity: 0.7;
}

/* ================================================
   RESPONSIVE
   ================================================ */
@media (max-width: 1199.98px) {
    .inv-sum {
        grid-template-columns: 90px minmax(0, 1fr) auto auto auto auto;
        grid-template-areas:
            "img product loc loc act act"
            "img qty stats stats stats stats"
            "img stats2 stats2 stats2 stats2 stats2";
        row-gap: 10px;
        padding: 14px;
    }
    .inv-col-img {
        grid-area: img;
        width: 90px;
        height: 90px;
    }
    .inv-col-product {
        grid-area: product;
    }
    .inv-col-loc {
        grid-area: loc;
    }
    .inv-col-qty-input {
        grid-area: qty;
    }
    .inv-col-num {
        display: none;
    }
    .inv-col-num:nth-child(5),
    .inv-col-num:nth-child(6),
    .inv-col-num:nth-child(7) {
        display: flex;
        grid-area: stats;
        grid-auto-flow: column;
        gap: 16px;
        justify-content: flex-start;
        text-align: left;
        margin-top: 6px;
        padding-top: 8px;
        border-top: 1px dashed #ede8e1;
    }
    .inv-col-num:nth-child(5) {
        grid-area: stats;
    }
    .inv-col-num:nth-child(6) {
        grid-area: stats2;
    }
    .inv-col-num:nth-child(7) {
        grid-area: stats2;
    }
    .inv-col-num:nth-child(6),
    .inv-col-num:nth-child(7) {
        margin-top: 0;
        padding-top: 0;
        border-top: 0;
    }
    .inv-col-actions {
        grid-area: act;
        align-self: start;
    }
}

@media (max-width: 767.98px) {
    .inv-page {
        width: 100%;
        padding: 0 8px;
    }
    .inv-toolbar {
        padding: 8px !important;
    }
    .inv-search-group {
        flex: 1 1 100%;
        max-width: none;
        margin-left: 0;
    }

    .inv-sum {
        grid-template-columns: 80px 1fr auto;
        grid-template-areas:
            "img  product  act"
            "loc  loc      loc"
            "qty  qty      qty"
            "stats stats   stats";
        row-gap: 10px;
        padding: 12px;
    }
    .inv-col-img {
        grid-area: img;
        width: 80px;
        height: 80px;
    }
    .inv-col-product {
        grid-area: product;
    }
    .inv-col-loc {
        grid-area: loc;
    }
    .inv-col-loc .pc-select {
        width: 100%;
    }
    .inv-col-qty-input {
        grid-area: qty;
        justify-content: flex-start;
    }
    .inv-col-qty-input::before {
        content: "QUANTITY";
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.05em;
        color: var(--pc-muted);
        margin-right: 8px;
    }
    .inv-col-num {
        display: flex !important;
        grid-area: stats;
        flex-direction: row !important;
        gap: 20px;
        justify-content: flex-start;
        text-align: left;
        padding-top: 8px;
        border-top: 1px dashed #ede8e1;
    }
    .inv-col-num:nth-child(6),
    .inv-col-num:nth-child(7) {
        grid-area: stats;
        border-top: 0;
        padding-top: 0;
    }
    .inv-col-num:nth-child(5),
    .inv-col-num:nth-child(6),
    .inv-col-num:nth-child(7) {
        margin-top: 0;
    }
    .inv-col-actions {
        grid-area: act;
        align-self: start;
    }
}

@media (max-width: 399.98px) {
    .inv-col-img {
        width: 64px;
        height: 64px;
    }
    .inv-col-num {
        flex-wrap: wrap;
    }
}
</style>

<style>
@import url("https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@600;700;800&display=swap");

.pl-page,
.pl-modal {
    --pl-ink: #221f1c;
    --pl-ink-2: #34302b;
    --pl-orange: #ee7a22;
    --pl-orange-dark: #d4620f;
    --pl-orange-soft: #fff1e4;
    --pl-teal: #0f6e6b;
    --pl-teal-soft: #e2f2f0;
    --pl-red: #c93c30;
    --pl-red-soft: #fbe9e7;
    --pl-amber: #c98a12;
    --pl-amber-soft: #fdf3de;
    --pl-line: #e5e1da;
    --pl-mist: #f6f4f0;
    --pl-muted: #736c63;
    font-family: "Manrope", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    color: var(--pl-ink);
}
@media (pointer: coarse) {
    .pl-page .pc-icon-btn {
        width: 42px !important;
        height: 40px !important;
    }
    .pl-page .pc-btn {
        height: 40px;
    }
    .pl-page .pc-input.form-control,
    .pl-page .pc-select.form-control {
        height: 44px;
        font-size: 16px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/Inventory/AddInventory.vue